import React, { useState, useEffect } from "react";
import { Col, Row, Card, Table, Button, Spinner, Form, Dropdown } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";

// Custom Multi-Select Dropdown Component using checkboxes
const MultiSelectDropdown = ({ title, options, selectedValues, onChange }) => {
  const handleToggle = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const allSelected = options.length > 0 && selectedValues.length === options.length;

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value ?? opt));
    }
  };

  return (
    <Dropdown className="w-100">
      <Dropdown.Toggle 
        as="div"
        className="form-select w-100 text-start d-flex justify-content-between align-items-center" 
        style={{ 
          cursor: 'pointer', 
          borderColor: '#ced4da', 
          borderRadius: '4px', 
          height: '38px',
          padding: '0.375rem 2.25rem 0.375rem 0.75rem',
          backgroundColor: '#FFF'
        }}
      >
        <span className="text-truncate">
          {selectedValues.length === 0 
            ? `Tous/Toutes (${title})` 
            : `${selectedValues.length} sélectionné(s)`}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="w-100 shadow-sm" style={{ maxHeight: '250px', overflowY: 'auto', padding: '6px' }}>
        <Dropdown.Item 
          as="button"
          onClick={handleToggleAll}
          className="d-flex justify-content-between align-items-center py-2 px-3 border-0 border-bottom mb-2 fw-bold"
          style={{
            backgroundColor: allSelected ? '#007BFF' : 'transparent',
            color: allSelected ? '#FFF' : '#333',
            fontSize: '0.88rem',
            borderRadius: '4px'
          }}
        >
          <span>Tous</span>
          {allSelected && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.78rem', color: '#FFF' }} />}
        </Dropdown.Item>
        {options.map((opt, index) => {
          const value = opt.value ?? opt;
          const label = opt.label ?? opt;
          const isChecked = selectedValues.includes(value);
          return (
            <Dropdown.Item 
              key={index}
              as="button"
              onClick={() => handleToggle(value)}
              className="d-flex justify-content-between align-items-center py-2 px-3 border-0"
              style={{
                backgroundColor: isChecked ? '#007BFF' : 'transparent',
                color: isChecked ? '#FFF' : '#333',
                fontSize: '0.88rem',
                borderRadius: '4px',
                marginBottom: '2px'
              }}
            >
              <span>{label}</span>
              {isChecked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.78rem', color: '#FFF' }} />}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default function RapportGeneral() {
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentSearchQuery, setAgentSearchQuery] = useState("");

  // Filters state
  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedDraws, setSelectedDraws] = useState([]);

  // Confirmed filter values for querying/display
  const [confirmedStart, setConfirmedStart] = useState(todayStr);
  const [confirmedEnd, setConfirmedEnd] = useState(todayStr);
  const [confirmedBranch, setConfirmedBranch] = useState("");
  const [confirmedAgents, setConfirmedAgents] = useState([]);
  const [confirmedDraws, setConfirmedDraws] = useState([]);

  // Fetch filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const agentsData = await client("agents/");
        const agentsList = agentsData ? (agentsData.results || (Array.isArray(agentsData) ? agentsData : [])) : [];
        setAgents(agentsList);

        const branchesData = await client("branches/");
        const branchesList = branchesData ? (branchesData.results || (Array.isArray(branchesData) ? branchesData : [])) : [];
        setBranches(branchesList);

        const drawsData = await client("draw-configs/");
        const drawsList = drawsData.draws || (Array.isArray(drawsData) ? drawsData : []);
        if (drawsList && drawsList.length > 0) {
          setDraws(drawsList.map(item => item.draw));
        } else {
          setDraws([
            { id: 1, name: "Florida matin" },
            { id: 2, name: "Florida soir" },
            { id: 3, name: "New-york matin" },
            { id: 4, name: "New-york soir" },
            { id: 5, name: "Georgia-Matin" }
          ]);
        }
      } catch (err) {
        console.error("Error loading filters:", err);
      }
    };
    loadFilters();
  }, []);

  // Fetch report data
  const fetchReportData = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await client(`dashboard/stats/?start_date=${confirmedStart}&end_date=${confirmedEnd}`);
      if (data) {
        setStats(data);
      }
    } catch (err) {
      console.error("Error fetching report stats:", err);
    } finally {
      setLoading(false);
    }
  }, [confirmedStart, confirmedEnd]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleValidateFilters = () => {
    setConfirmedStart(startDate);
    setConfirmedEnd(endDate);
    setConfirmedBranch(selectedBranch);
    setConfirmedAgents(selectedAgents);
    setConfirmedDraws(selectedDraws);
  };

  const formatVal = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? "0" : String(Math.round(num));
  };

  // Helper to filter results
  const getFilteredData = () => {
    if (!stats) return [];
    
    // Filter agents
    let rows = stats.agent_stats || [];
    
    if (confirmedAgents.length > 0) {
      rows = rows.filter(a => confirmedAgents.includes(a.username));
    }

    if (confirmedBranch) {
      const branchObj = branches.find(b => b.id === confirmedBranch || b.name === confirmedBranch);
      const branchName = branchObj ? branchObj.name : confirmedBranch;
      rows = rows.filter(a_stat => {
        const fullAgent = agents.find(ag => ag.username === a_stat.username);
        return fullAgent && (fullAgent.branch_name === branchName || fullAgent.branch_id === confirmedBranch);
      });
    }

    return rows;
  };

  const reportRows = getFilteredData();

  // Local filter for agents performance table
  const filteredReportRows = reportRows.filter(agent => {
    const query = agentSearchQuery.toLowerCase();
    const username = (agent.username || "").toLowerCase();
    const firstName = (agent.first_name || "").toLowerCase();
    const lastName = (agent.last_name || "").toLowerCase();
    const agentId = (agent.id || "").toLowerCase();
    return username.includes(query) || firstName.includes(query) || lastName.includes(query) || agentId.includes(query);
  });

  const rowColor = (balance) => (parseFloat(balance) < 0 ? '#DC3545' : '#28A745');

  const agentTotals = filteredReportRows.reduce((acc, agent) => ({
    tickets_count: acc.tickets_count + (parseInt(agent.tickets_count, 10) || 0),
    sales: acc.sales + (parseFloat(agent.sales) || 0),
    payouts: acc.payouts + (parseFloat(agent.payouts) || 0),
    commissions: acc.commissions + (parseFloat(agent.commissions) || 0),
    sup_commissions: acc.sup_commissions + (parseFloat(agent.sup_commissions) || 0),
    b_final: acc.b_final + (parseFloat(agent.b_final) || 0),
  }), { tickets_count: 0, sales: 0, payouts: 0, commissions: 0, sup_commissions: 0, b_final: 0 });

  // Calculations for totals
  const totalSales = reportRows.reduce((acc, row) => acc + parseFloat(row.sales || 0), 0);
  const totalComms = reportRows.reduce((acc, row) => acc + parseFloat(row.commissions || 0), 0);
  const totalPayouts = reportRows.reduce((acc, row) => acc + parseFloat(row.payouts || 0), 0);

  // Filter agent options based on selected branch
  const filteredAgentOptions = agents
    .filter(a => !selectedBranch || a.branch_name === selectedBranch)
    .map(a => ({ value: a.username, label: a.username }));

  const drawOptions = draws.map(d => ({ value: d.name, label: d.name }));

  return (
    <div style={{ padding: '24px 0' }}>
      <h3 className="mb-4" style={{ fontWeight: 'bold', color: '#222' }}>Rapport général</h3>

      {/* Filters Card */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '20px' }}>
        <Row className="mb-3 align-items-end">
          {/* Dates */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Début</label>
            <div className="position-relative d-flex align-items-center">
              <input
                type="date"
                className="form-control"
                style={{
                  borderColor: '#28A745',
                  paddingRight: '36px',
                  borderRadius: '4px'
                }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
            </div>
          </Col>
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Fin</label>
            <div className="position-relative d-flex align-items-center">
              <input
                type="date"
                className="form-control"
                style={{
                  borderColor: '#28A745',
                  paddingRight: '36px',
                  borderRadius: '4px'
                }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
            </div>
          </Col>

          {/* Succursale Filter */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Succursale</label>
            <Form.Select
              style={{ borderRadius: '4px', borderColor: '#CCC' }}
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setSelectedAgents([]); // Reset agents on branch change
              }}
            >
              <option value="">Toutes</option>
              {branches.map(b => (
                <option key={b.id} value={b.name}>{b.name}</option>
              ))}
            </Form.Select>
          </Col>

          {/* Agent Filter (Multi-select) */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Agent</label>
            <MultiSelectDropdown
              title="agents"
              options={filteredAgentOptions}
              selectedValues={selectedAgents}
              onChange={setSelectedAgents}
            />
          </Col>

          {/* Tirage Filter (Multi-select) */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="fw-bold small mb-1" style={{ color: '#333' }}>Tirage</label>
            <MultiSelectDropdown
              title="tirages"
              options={drawOptions}
              selectedValues={selectedDraws}
              onChange={setSelectedDraws}
            />
          </Col>

          {/* Filter Button */}
          <Col xs={12} md={2} className="mb-2 mb-md-0">
            <label className="d-none d-md-block">&nbsp;</label>
            <Button
              className="w-100"
              style={{
                backgroundColor: '#0069D9',
                borderColor: '#0062cc',
                color: '#FFF',
                fontWeight: '600',
                height: '38px',
                borderRadius: '4px'
              }}
              onClick={handleValidateFilters}
            >
              Filtrer
            </Button>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="sm" />
          <span className="ms-2 text-muted small">Chargement des données...</span>
        </div>
      ) : (
        <>
          {/* Summary Card (Daily Report Style) */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '24px' }}>
            <div className="text-center mb-3">
              <h4 style={{ fontWeight: 'bold', color: '#111', margin: '0 0 4px 0' }}>Rapport de vente</h4>
              <div className="small text-muted fw-bold">Succursale : {confirmedBranch || "Tout"}</div>
              <div className="small text-muted fw-bold">Tirage : {confirmedDraws.length === 0 ? "Tout" : confirmedDraws.join(", ")}</div>
            </div>

            <div className="mb-4" style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#333' }}>
              <div>Quantité POS : <strong>{reportRows.length}</strong></div>
              <div>Quantité POS FACTURABLE : <strong>{reportRows.filter(r => parseFloat(r.sales) > 0).length}</strong></div>
            </div>
            
            <div className="table-responsive">
              <Table bordered className="mb-0 text-start align-middle" style={{ borderColor: '#EAEAEA', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#FAF9F9', fontWeight: 'bold' }}>
                    <th style={{ padding: '10px', fontSize: '0.88rem', color: '#333', width: '50%' }}>Date début : {confirmedStart}</th>
                    <th style={{ padding: '10px', fontSize: '0.88rem', color: '#333', width: '50%' }}>Date fin : {confirmedEnd}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>TFiche : <strong>{stats?.tickets_count ?? 0}</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>Vente : <strong>{formatVal(totalSales)}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>Fiche gagnante : <strong>{stats?.winning_tickets_count ?? 0}</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>A payé : <strong>{formatVal(totalPayouts)}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>Fiche supprimée : <strong>{stats?.cancelled_tickets_count ?? 0}</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>Balance Sans %agent : <strong>{Math.round((totalSales - totalPayouts) || 0)}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>%Agent : <strong>{formatVal(totalComms)}</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>Balance Avec %agent : <strong>{Math.round((totalSales - totalComms - totalPayouts) || 0)}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>%Superviseur : <strong>0</strong></td>
                    <td style={{ padding: '12px', fontSize: '0.88rem' }}>Balance Avec %superviseur : <strong>{Math.round((totalSales - totalPayouts) || 0)}</strong></td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Card>

          {/* Aperçu par succursale */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '24px' }}>
            <Card.Header className="bg-white border-0 p-0 mb-3">
              <h4 style={{ fontWeight: 'bold', margin: 0, color: '#333' }}>Aperçu par succursale</h4>
            </Card.Header>
            <div className="table-responsive">
              <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#DDD' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F8F9FA' }}>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Succursale <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Total ventes <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      A payé <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Pourcentage <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Balance <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(!stats?.branch_stats || stats.branch_stats.length === 0) ? (
                    <tr>
                      <td colSpan={5} className="text-center py-3 text-muted">
                        Aucune donnée par succursale disponible.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {stats.branch_stats.map((branch, idx) => {
                        const sales = parseFloat(branch.sales) || 0;
                        const payouts = parseFloat(branch.payouts) || 0;
                        const balance = parseFloat(branch.balance) || 0;
                        const pct = sales > 0 ? ((payouts / sales) * 100).toFixed(1) : '0.0';
                        const color = balance < 0 ? '#DC3545' : '#28A745';
                        return (
                          <tr key={idx} style={{ verticalAlign: 'middle' }}>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'left', fontWeight: '500' }}>
                              {branch.name}
                            </td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(branch.sales)}
                            </td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(branch.payouts)}
                            </td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'center' }}>
                              {pct}%
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold' }}>
                              {formatVal(branch.balance)}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Ligne totaux */}
                      {(() => {
                        const totSales = stats.branch_stats.reduce((a, b) => a + (parseFloat(b.sales) || 0), 0);
                        const totPayouts = stats.branch_stats.reduce((a, b) => a + (parseFloat(b.payouts) || 0), 0);
                        const totBalance = stats.branch_stats.reduce((a, b) => a + (parseFloat(b.balance) || 0), 0);
                        const totPct = totSales > 0 ? ((totPayouts / totSales) * 100).toFixed(1) : '0.0';
                        const totColor = totBalance < 0 ? '#DC3545' : '#28A745';
                        return (
                          <tr style={{ verticalAlign: 'middle', backgroundColor: '#F8F9FA', fontWeight: 'bold' }}>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'left' }}>Total</td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{Math.round(totSales)}</td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{Math.round(totPayouts)}</td>
                            <td style={{ fontSize: '0.85rem', padding: '8px', textAlign: 'center' }}>{totPct}%</td>
                            <td style={{ fontSize: '0.85rem', color: totColor, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{Math.round(totBalance)}</td>
                          </tr>
                        );
                      })()}
                    </>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* New Agents Performance Table in Rapport General */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px', padding: '24px' }}>
            <Card.Header className="bg-white border-0 p-0 mb-3">
              <h4 style={{ fontWeight: 'bold', margin: 0, color: '#333' }}>Agents</h4>
            </Card.Header>

            {/* Action buttons + Search */}
            <div className="pb-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
              <div className="d-flex" style={{ gap: '4px' }}>
                {["COPIER", "EXCEL", "PDF", "IMPRIMER"].map((label, idx) => (
                  <button
                    key={idx}
                    style={{
                      backgroundColor: '#F5F5F5',
                      border: '1px solid #CCCCCC',
                      color: '#333333',
                      fontSize: '0.78rem',
                      padding: '6px 16px',
                      borderRadius: '2px',
                      height: '34px',
                      fontWeight: 'normal',
                      outline: 'none'
                    }}
                    onClick={() => alert(`${label} cliqué`)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <span className="fw-bold small text-dark" style={{ fontSize: '0.92rem' }}>Search:</span>
                <input 
                  type="text" 
                  className="form-control form-control-sm"
                  style={{ width: '220px', borderRadius: '4px', border: '1px solid #CCC' }} 
                  value={agentSearchQuery}
                  onChange={(e) => setAgentSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="table-responsive">
              <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#DDD' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F8F9FA' }}>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Agent <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Tfiche <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Vente <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      A payé <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      %Agent <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      %Sup <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                    <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                      Balance <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReportRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-3 text-muted">
                        Aucune performance agent trouvée pour cette sélection.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filteredReportRows.map((agent, idx) => {
                        const color = rowColor(agent.b_final);
                        return (
                          <tr key={idx} style={{ verticalAlign: 'middle' }}>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'left' }}>
                              {agent.first_name || agent.last_name ? `${agent.first_name} ${agent.last_name}` : agent.username}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'center' }}>
                              {agent.tickets_count}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.sales)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.payouts)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.commissions)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.sup_commissions)}
                            </td>
                            <td style={{ fontSize: '0.85rem', color, padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {formatVal(agent.b_final)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ verticalAlign: 'middle', backgroundColor: '#F8F9FA', fontWeight: 'bold' }}>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'left' }}>
                          Total
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'center' }}>
                          {agentTotals.tickets_count}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.sales)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.payouts)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.commissions)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.sup_commissions)}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: rowColor(agentTotals.b_final), padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {formatVal(agentTotals.b_final)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>

          {/* Print Button */}
          <div className="text-center mt-4 mb-5">
            <Button 
              variant="primary" 
              onClick={() => window.print()}
              style={{ padding: '10px 30px', fontWeight: '600' }}
            >
              Imprimer
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
